import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { X, CreditCard, Send, MessageSquare, Mail } from 'lucide-react';

function CardForm({ clientId, onSuccess, onError, apiService }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState(null);

  useEffect(() => {
    const createIntent = async () => {
      try {
        const data = await apiService.createSetupIntent(clientId);
        setSetupIntentId(data.setup_intent_id);
      } catch (err) {
        onError(err.message || 'Failed to initialize card form');
      }
    };
    createIntent();
  }, [clientId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !setupIntentId) return;

    setProcessing(true);
    try {
      const { error, setupIntent } = await stripe.confirmCardSetup(
        (await apiService.createSetupIntent(clientId)).client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
          },
        }
      );

      if (error) {
        onError(error.message);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        const result = await apiService.confirmCardSaved(clientId, setupIntent.id);
        onSuccess(result);
      }
    } catch (err) {
      onError(err.message || 'Failed to save card');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="border border-gray-300 rounded p-3 mb-3 bg-white">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '14px',
                color: '#333',
                '::placeholder': { color: '#aab7c4' },
              },
              invalid: { color: '#dc2626' },
            },
          }}
        />
      </div>
      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full px-3 py-2 text-xs bg-red-800 text-white rounded hover:bg-red-900 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
      >
        <CreditCard className="w-3.5 h-3.5" />
        {processing ? 'Saving...' : 'Save Card'}
      </button>
    </form>
  );
}

export default function AddCardModal({ isOpen, onClose, clientId, clientName, clientPhone, apiService, onCardSaved }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [sendingLink, setSendingLink] = useState(false);
  const [linkSent, setLinkSent] = useState(null);

  useEffect(() => {
    if (isOpen && !stripePromise) {
      const loadKey = async () => {
        try {
          const data = await apiService.getStripeKey();
          setStripePromise(loadStripe(data.publishable_key));
        } catch (err) {
          setError('Failed to load payment form');
        }
      };
      loadKey();
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleSuccess = (result) => {
    setSuccess(`Card saved: ${result.card_brand} ****${result.card_last_four}`);
    setError(null);
    if (onCardSaved) onCardSaved(result);
  };

  const handleSendLink = async (method) => {
    setSendingLink(true);
    try {
      await apiService.sendCardLink(clientId, method);
      setLinkSent(method);
    } catch (err) {
      setError(err.message || 'Failed to send link');
    } finally {
      setSendingLink(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2">
      <div className="bg-white border border-gray-200 shadow-lg max-w-md w-full mx-4">
        <div className="bg-red-800 text-white px-3 py-2 flex items-center justify-between border-b border-red-900">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Add Card — {clientName}
          </h3>
          <button onClick={onClose} className="text-white hover:text-gray-200">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-3 space-y-3">
          {error && (
            <div className="bg-red-50 border border-red-200 p-2 text-xs text-red-800 rounded">
              {error}
            </div>
          )}

          {success ? (
            <div className="bg-green-50 border border-green-200 p-3 text-center rounded">
              <CreditCard className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-green-800">{success}</p>
              <button
                onClick={onClose}
                className="mt-2 px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {/* Manual Entry */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Enter card details</p>
                {stripePromise ? (
                  <Elements stripe={stripePromise}>
                    <CardForm
                      clientId={clientId}
                      onSuccess={handleSuccess}
                      onError={(msg) => setError(msg)}
                      apiService={apiService}
                    />
                  </Elements>
                ) : (
                  <p className="text-xs text-gray-500">Loading payment form...</p>
                )}
              </div>

              {/* Divider */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <div className="flex-1 h-px bg-gray-200" />
              </div>

              {/* Send Link */}
              <div>
                <p className="text-xs font-medium text-gray-700 mb-2">Send card link to client</p>
                {linkSent ? (
                  <p className="text-xs text-green-600">Link sent via {linkSent}!</p>
                ) : (
                  <div className="flex gap-2">
                    {clientPhone && (
                      <button
                        onClick={() => handleSendLink('sms')}
                        disabled={sendingLink}
                        className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                      >
                        <MessageSquare className="w-3.5 h-3.5" />
                        {sendingLink ? 'Sending...' : 'Send SMS'}
                      </button>
                    )}
                    <button
                      onClick={() => handleSendLink('email')}
                      disabled={sendingLink}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs bg-white border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                    >
                      <Mail className="w-3.5 h-3.5" />
                      {sendingLink ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
